import React, { useState, useEffect, useRef } from 'react';
import api from '../../api';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import SuccessModal from '../../components/common/SuccessModal';
import FailModal from '../../components/common/FailModal';
import { useSelection } from '../../hooks/useSelection';
import { validateField } from '../../utils/validation';
import { usePagination } from '../../hooks/usePagination';
import TomSelect from '../../components/common/TomSelect';
import { GenericView, SearchInput } from '../../components/common/table';
import { useAuth } from '../../contexts/AuthProvider';

const ProductLevel = () => {
  // Actions dropdown refs for manual Bootstrap Dropdown initialization
  const actionsToggleRef = useRef(null);
  const actionsContainerRef = useRef(null);
  const actionsDropdownInstanceRef = useRef(null);
  const [isActionsOpen, setIsActionsOpen] = useState(false);

  useEffect(() => {
    const toggleEl = actionsToggleRef.current;
    const containerEl = actionsContainerRef.current;
    if (!toggleEl || !containerEl) return;
    if (typeof window !== 'undefined' && window.bootstrap?.Dropdown) {
      const instance = new window.bootstrap.Dropdown(toggleEl, { autoClose: true });
      actionsDropdownInstanceRef.current = instance;
      const onShow = () => setIsActionsOpen(true);
      const onHide = () => setIsActionsOpen(false);
      containerEl.addEventListener('show.bs.dropdown', onShow);
      containerEl.addEventListener('hide.bs.dropdown', onHide);
      return () => {
        containerEl.removeEventListener('show.bs.dropdown', onShow);
        containerEl.removeEventListener('hide.bs.dropdown', onHide);
        try { instance.dispose(); } catch {}
        actionsDropdownInstanceRef.current = null;
      };
    }
  }, []);

  // Permission system
  const { user, can, capabilitiesLoading } = useAuth();
  const canProductLevel = (key) => can(key, 'productLevel');

    // Pagination
    const pagination = usePagination(10);
    const [productLevels, setProductLevels] = useState([]);

    // Date filter
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    // Search
    const [search, setSearch] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');

    const handleSearchInputChange = (value) => {
        setSearch(value);
        setSearchTerm(value);
    };

    const handleSearchSubmit = () => {
        setAppliedSearchTerm(searchTerm);
        pagination.setPage(0);
    };

    // Selection
    const {
        selectedIds,
        setSelectedIds,
        handleCheckboxClick,
        handleSelectAll
    } = useSelection();

    // Data and form state
    const [formData, setFormData] = useState({
        name: '',
        productCategoryIds: []
    });
    const [editingId, setEditingId] = useState(null);
    const [errors, setErrors] = useState({});
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [confirmAction, setConfirmAction] = useState(null);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showFailModal, setShowFailModal] = useState(false);
    const [failMessage, setFailMessage] = useState('');
    const [productCategories, setProductCategories] = useState([]);

    const fetchProductLevels = async (searchValue = search) => {
        try {
            const response = await api.get(`/md/product-level-page`, {
                params: {
                    page: pagination.page,
                    size: pagination.size,
                    search: searchValue,
                    from: fromDate,
                    to: toDate
                }
            });
            setProductLevels(response.data.content);
            pagination.setTotalPages(response.data.totalPages);
            pagination.setTotal(response.data.totalElements);
            pagination.setCurrentPageItems(response.data.content.length);
        } catch (error) {
            let errorMessage = 'Failed to fetch product levels.';
            let technicalDetails = '';

            if (error.response) {
                switch (error.response.status) {
                    case 403:
                        errorMessage = 'You do not have permission to view product levels.';
                        technicalDetails = 'Required role or permission is missing';
                        break;
                    case 500:
                        errorMessage = 'Server error while fetching product levels.';
                        technicalDetails = error.response.data.message || 'Internal server error occurred';
                        break;
                    default:
                        errorMessage = 'An error occurred while fetching product levels.';
                        technicalDetails = `Server responded with status: ${error.response.status}`;
                }
            } else if (error.request) {
                errorMessage = 'Unable to connect to the server.';
                technicalDetails = 'Network error or server is not responding';
            } else {
                errorMessage = 'An unexpected error occurred.';
                technicalDetails = error.message;
            }

            setProductLevels([]);
            setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
            setShowFailModal(true);
        }
    };

    const fetchProductCategories = async () => {
        try {
            const response = await api.get('/md/product-category');
            setProductCategories(response.data);
        } catch (error) {
            let errorMessage = 'Failed to fetch product categories.';
            let technicalDetails = '';

            if (error.response) {
                switch (error.response.status) {
                    case 403:
                        errorMessage = 'You do not have permission to view product categories.';
                        technicalDetails = 'Required role or permission is missing';
                        break;
                    case 500:
                        errorMessage = 'Server error while fetching product categories.';
                        technicalDetails = error.response.data.message || 'Internal server error occurred';
                        break;
                    default:
                        errorMessage = 'An error occurred while fetching product categories.';
                        technicalDetails = `Server responded with status: ${error.response.status}`;
                }
            } else if (error.request) {
                errorMessage = 'Unable to connect to the server.';
                technicalDetails = 'Network error or server is not responding';
            } else {
                errorMessage = 'An unexpected error occurred.';
                technicalDetails = error.message;
            }

            setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
            setShowFailModal(true);
            setProductCategories([]);
        }
    };

    useEffect(() => {
        fetchProductLevels(appliedSearchTerm);
        fetchProductCategories();
    }, [pagination.page, pagination.size, fromDate, toDate, appliedSearchTerm]);

    // Open modal for create
    const handleCreate = () => {
    if (!canProductLevel('create')) { setFailMessage('You do not have permission to create.'); setShowFailModal(true); return; }
        setFormData({
            name: '',
            productCategoryIds: []
        });
        setEditingId(null);
        setErrors({});
        const modal = new window.bootstrap.Modal(document.getElementById('modal-product-level'));
        modal.show();
    };

    // Open modal for edit
    const handleEdit = (item) => {
    if (!canProductLevel('edit')) { setFailMessage('You do not have permission to edit.'); setShowFailModal(true); return; }
        setFormData({
            name: item.name,
            productCategoryIds: item.productCategories?.map(category => category.id) || []
        });
        setEditingId(item.id);
        setErrors({});
        const modal = new window.bootstrap.Modal(document.getElementById('modal-product-level'));
        modal.show();
    };

    // Real-time validation
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        let error = null;
        if (name === 'name') {
            error = validateField('name', value, { required: true });
        }
        setErrors((prev) => ({ ...prev, [name]: error }));
    };

    // TomSelect change handler
    const handleSelectChange = (name, value) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
        let error = null;
        if (name === 'productCategoryIds') {
            error = value.length === 0 ? 'At least one Product Category is required' : null;
        }
        setErrors((prev) => ({ ...prev, [name]: error }));
    };

    // Unified handleSubmit for both create and edit
    const handleSubmit = async (e) => {
        e.preventDefault();
        const error = validateField('name', formData.name, { required: true });
        const productCategoryError = formData.productCategoryIds.length === 0 ? 'At least one Product Category is required' : null;

        setErrors({
            name: error,
            productCategoryIds: productCategoryError
        });

        if (error || productCategoryError) return;

        try {
            const payload = {
                name: formData.name,
                productCategoryIds: formData.productCategoryIds
            };

            if (editingId) {
                await api.put(`/md/product-level/${editingId}`, payload);
                setSuccessMessage('Product level has been successfully updated.');
            } else {
                await api.post('/md/product-level', payload);
                setSuccessMessage('Product level has been successfully added.');
            }
            setShowSuccessModal(true);
            setEditingId(null);
            setFormData({
                name: '',
                productCategoryIds: []
            });
            setErrors({});
            await fetchProductLevels();
            const modal = document.getElementById('modal-product-level');
            const bootstrapModal = window.bootstrap?.Modal.getInstance(modal);
            if (bootstrapModal) bootstrapModal.hide();
        } catch (error) {
            let errorMessage = 'Failed to save product level.';
            let technicalDetails = '';

            if (error.response) {
                switch (error.response.status) {
                    case 409:
                        errorMessage = 'A product level with this name already exists.';
                        technicalDetails = `Duplicate entry for name: "${formData.name}"`;
                        break;
                    case 400:
                        errorMessage = 'Invalid data provided.';
                        technicalDetails = error.response.data.message || 'The server rejected the request due to invalid data';
                        break;
                    case 403:
                        errorMessage = 'You do not have permission to perform this action.';
                        technicalDetails = 'Required role or permission is missing for this operation';
                        break;
                    case 404:
                        errorMessage = 'Product level not found.';
                        technicalDetails = 'The requested item may have been deleted or does not exist';
                        break;
                    case 500:
                        errorMessage = 'Server encountered an error while processing the request.';
                        technicalDetails = error.response.data.message || 'Internal server error occurred';
                        break;
                    default:
                        errorMessage = 'An error occurred while saving the product level.';
                        technicalDetails = `Server responded with status: ${error.response.status}`;
                }
            } else if (error.request) {
                errorMessage = 'Unable to connect to the server.';
                technicalDetails = 'Network error or server is not responding';
            } else {
                errorMessage = 'An unexpected error occurred.';
                technicalDetails = error.message;
            }

            setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
            setShowFailModal(true);
        }
    };

    // Edit selected (only works for single selection)
    const editSelected = () => {
        if (selectedIds.length !== 1) {
            setSuccessMessage('Please select exactly one product level to edit');
            setShowSuccessModal(true);
            return;
        }
        const selectedId = selectedIds[0];
        const selectedItem = productLevels.find(item => item.id === selectedId);
        if (selectedItem) {
            handleEdit(selectedItem);
        }
    };

    // Delete selected (bulk)
    const deleteSelected = () => {
    if (!canProductLevel('delete')) { setFailMessage('You do not have permission to delete.'); setShowFailModal(true); return; }
        if (selectedIds.length === 0) {
            setFailMessage('Please select at least one product level to delete.\n\nTechnical Details: No items selected for deletion');
            setShowFailModal(true);
            return;
        }
        setConfirmAction(() => async () => {
            try {
                await api.delete('/md/product-level/bulk', { data: selectedIds });
                setSelectedIds([]);
                fetchProductLevels();
                setShowDeleteConfirmation(false);
                setSuccessMessage('Selected product levels deleted successfully');
                setShowSuccessModal(true);
            } catch (error) {
                let errorMessage = 'Failed to delete product levels.';
                let technicalDetails = '';

                if (error.response) {
                    switch (error.response.status) {
                        case 403:
                            errorMessage = 'You do not have permission to delete these items.';
                            technicalDetails = 'Required role or permission is missing for deletion';
                            break;
                        case 400:
                            errorMessage = 'Invalid delete request.';
                            technicalDetails = error.response.data.message || 'The server rejected the deletion request';
                            break;
                        case 404:
                            errorMessage = 'One or more items not found.';
                            technicalDetails = 'Some items may have been already deleted';
                            break;
                        case 500:
                            errorMessage = 'Server error while deleting items.';
                            technicalDetails = error.response.data.message || 'Internal server error during deletion';
                            break;
                        default:
                            errorMessage = 'An error occurred while deleting the items.';
                            technicalDetails = `Server responded with status: ${error.response.status}`;
                    }
                } else if (error.request) {
                    errorMessage = 'Unable to connect to the server.';
                    technicalDetails = 'Network error or server is not responding';
                } else {
                    errorMessage = 'An unexpected error occurred during deletion.';
                    technicalDetails = error.message;
                }

                setShowDeleteConfirmation(false);
                setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
                setShowFailModal(true);
            }
        });
        setShowDeleteConfirmation(true);
    };

    const columns = [
        { key: 'name', label: 'Name', sortable: true },
        { key: 'productCategories', label: 'Product Categories', sortable: false, render: (value) => Array.isArray(value) ? value.map(c => c.name).join(', ') : '-' },
        { key: 'cuser', label: 'Created By', sortable: false, render: (value, row) => row.cuser?.username || '-' },
        { key: 'cdate', label: 'Created Date', sortable: true, render: (value) => value ? new Date(value).toLocaleDateString() : '-' },
    ];

    return (
        <>
                {/* Page header */}
                <div className="page-header d-print-none">
                    <div className="container-xl">
                        <div className="row g-2 align-items-center">
                            <div className="col">
                                <h2 className="page-title">Product Level</h2>
                            </div>
                            <div className="col-auto ms-auto d-print-none">
                                <div className="btn-list">
                                    <a href="#" className="btn btn-primary d-none d-sm-inline-block"
                                        onClick={handleCreate}>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24"
                                            strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                            <line x1="12" y1="5" x2="12" y2="19" />
                                            <line x1="5" y1="12" x2="19" y2="12" />
                                        </svg>
                                        Create
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Page body */}
                <div className="page-body">
                    <div className="container-xl">
                        <div className="card">
                            <div className="card-body">
                                <GenericView
                                    data={productLevels}
                                    columns={columns}
                                    isLoading={capabilitiesLoading}
                                    emptyStateMessage="No product levels found"
                                    checkboxSelection={true}
                                    selectedRows={selectedIds}
                                    onSelectRow={(itemId, isSelected) => {
                                        if (isSelected) {
                                            setSelectedIds([...selectedIds, itemId]);
                                        } else {
                                            setSelectedIds(selectedIds.filter(id => id !== itemId));
                                        }
                                    }}
                                    onSelectAllRows={(itemIds) => {
                                        if (Array.isArray(itemIds) && itemIds.length > 0) {
                                            setSelectedIds([...new Set([...selectedIds, ...itemIds])]);
                                        } else {
                                            setSelectedIds([]);
                                        }
                                    }}
                                    onEdit={canProductLevel('edit') ? (item) => {
                                        setSelectedIds([item.id]);
                                        handleEdit(item);
                                    } : undefined}
                                    onDelete={canProductLevel('delete') ? (item) => {
                                        setSelectedIds([item.id]);
                                        deleteSelected();
                                    } : undefined}
                                    searchComponent={
                                        <div className="d-flex gap-2">
                                            <SearchInput
                                                value={searchTerm}
                                                onChange={handleSearchInputChange}
                                                onKeyPress={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        handleSearchSubmit();
                                                    }
                                                }}
                                                placeholder="Search..."
                                                uniqueId="product-level-search"
                                            />
                                            <button type="button" className="btn btn-primary" onClick={handleSearchSubmit}>
                                                Search
                                            </button>
                                        </div>
                                    }
                                    actionButtons={
                                        <div className="d-flex gap-2">
                                            {(canProductLevel('edit') || canProductLevel('delete')) && (
                                                <div ref={actionsContainerRef} className="dropdown">
                                                    <a
                                                        ref={actionsToggleRef}
                                                        href="#"
                                                        className="btn dropdown-toggle"
                                                        data-bs-toggle="dropdown"
                                                        aria-expanded={isActionsOpen}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            const inst = actionsDropdownInstanceRef.current;
                                                            if (inst?.toggle) inst.toggle();
                                                            else setIsActionsOpen(prev => !prev);
                                                        }}
                                                    >Actions</a>
                                                    <div className="dropdown-menu">
                                                        {canProductLevel('edit') && (
                                                            <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); editSelected(); }}
                                                                style={{ color: selectedIds.length === 1 ? "" : "#ccc", pointerEvents: selectedIds.length === 1 ? "auto" : "none" }}>
                                                                Edit
                                                            </a>
                                                        )}
                                                        {canProductLevel('delete') && (
                                                            <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); deleteSelected(); }}
                                                                style={{ color: selectedIds.length ? "" : "#ccc", pointerEvents: selectedIds.length ? "auto" : "none" }}>
                                                                Delete
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    }
                                    pagination={{
                                        currentPage: pagination.page + 1,
                                        totalRows: pagination.total,
                                        rowsPerPage: pagination.size,
                                        onPageChange: (page) => pagination.setPage(page - 1),
                                        onRowsPerPageChange: (size) => {
                                            pagination.setSize(size);
                                            pagination.setPage(0);
                                        }
                                    }}
                                    pageSizeOptions={[10, 25, 50, 100]}
                                    uniqueId="product-level-table"
                                />
                            </div>
                        </div>
                    </div>
                </div>

            {/* Modal for add/edit product level */}
            <div className="modal modal-blur fade" id="modal-product-level" tabIndex="-1" role="dialog" aria-hidden="true">
                <div className="modal-dialog modal-lg" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">{editingId ? 'Edit Product Level' : 'Add New Product Level'}</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label required">Name</label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                        name="name"
                                        placeholder="Enter product level name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                    />
                                    {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                </div>
                                <div className="mb-3">
                                    <TomSelect
                                        name="productCategoryIds"
                                        value={formData.productCategoryIds}
                                        onChange={handleSelectChange}
                                        options={productCategories.map(category => ({ id: category.id, name: category.name }))}
                                        placeholder="Select Product Categories"
                                        label="Product Categories"
                                        required={true}
                                        error={errors.productCategoryIds}
                                        valueField="id"
                                        labelField="name"
                                        searchField="name"
                                        multiple={true}
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <a href="#" className="btn btn-link link-secondary" data-bs-dismiss="modal">
                                    Cancel
                                </a>
                                <button type="submit" className="btn btn-primary ms-auto">
                                    {editingId ? 'Update' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={showDeleteConfirmation}
                onClose={() => setShowDeleteConfirmation(false)}
                onConfirm={confirmAction || (() => { })}
                title="Delete Confirmation"
                message={selectedIds.length > 0
                    ? "Are you sure you want to delete the selected product levels? This action cannot be undone."
                    : "Are you sure you want to delete this product level? This action cannot be undone."}
                confirmText="Delete"
                cancelText="Cancel"
                type="danger"
            />

            {/* Success Modal */}
            <SuccessModal
                isOpen={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                title="Success"
                message={successMessage}
                buttonText="OK"
            />

            {/* Fail Modal */}
            <FailModal
                isOpen={showFailModal}
                onClose={() => setShowFailModal(false)}
                title="Operation Failed"
                message={failMessage}
                buttonText="Close"
            />
        </>
    );
};

export default ProductLevel; 